<template>
  <div class="form-wrapper">
    <div class="form-title">{{ title || $t('服务咨询') }}</div>
    <a-form ref="MyForm" :model="form" layout="vertical" class="form" @submit="handleSubmit">
      <a-form-item field="fullName" :label="$t('公司名称')">
        <a-input v-model="form.fullName" :placeholder="$t('请输入您的公司名称')" />
      </a-form-item>
      <a-form-item field="contactFullName" :label="$t('联系人')">
        <a-input v-model="form.contactFullName" :placeholder="$t('请输入联系人')" />
      </a-form-item>
      <a-form-item field="contactTelephone" :label="$t('联系电话')" :rules="mobileRule">
        <a-input v-model="form.contactTelephone" :placeholder="$t('请输入您的电话号码')" />
      </a-form-item>
      <a-form-item :label="$t('您关注的服务（可多选）')" class="select-service">
        <section class="items">
          <section class="item_radio_box" :class="{ selected: form.landing }" @click="form.landing = !form.landing">
            <span class="text">{{ $t('海外雇佣') }}</span>
            <Checkbox v-model="form.landing" @click.stop />
          </section>
          <section :class="{ selected: form.contract }" class="item_radio_box" @click="form.contract = !form.contract">
            <span class="text">{{ $t('灵活用工') }}</span>
            <Checkbox v-model="form.contract" @click.stop />
          </section>
          <section class="item_radio_box" :class="{ selected: form.service }" @click="form.service = !form.service">
            <span class="text">{{ $t('海外薪酬') }}</span>
            <Checkbox v-model="form.service" @click.stop />
          </section>
          <section
            class="item_radio_box"
            :class="{ selected: form.globalRecruitment }"
            @click="form.globalRecruitment = !form.globalRecruitment"
          >
            <span class="text">{{ $t('海外招聘') }}</span>
            <Checkbox v-model="form.globalRecruitment" @click.stop />
          </section>
        </section>
      </a-form-item>
      <a-button type="primary" class="submit_button" html-type="submit" :loading="btnLoading">
        {{ $t('提交') }}
      </a-button>
    </a-form>
  </div>
</template>

<script setup lang="ts">
import { Form, Message, Checkbox } from '@arco-design/web-vue';
import '@arco-design/web-vue/es/message/style/css.js';
import { getUserBrowseTrack } from '@/common/utils';
import { useI18n } from 'vue-i18n';

interface IProps {
  targetCountry?: string;
  entry: string;
  title?: string;
}
const { t } = useI18n();
const props = defineProps<IProps>();
const route = useRoute();
const { bd_vid } = route.query as { bd_vid?: string };

const MyForm = shallowRef<InstanceType<typeof Form> | null>(null);
const form = reactive({
  contactFullName: '',
  fullName: '',
  contactTelephone: '',
  contactEmail: '',
  landing: false,
  contract: false,
  service: false,
  globalRecruitment: false,
  targetCountry: '',
});
const btnLoading = ref(false);

let userBrowseTrack = '';
async function handleSubmit({ values, errors }: any) {
  if (errors) {
    return;
  }
  try {
    useGtag();
    btnLoading.value = true;
    values.concernedServices = handleRequestParams();
    values.page = route.fullPath;
    values.entry = props.entry || '未知';
    values.userBrowseTrack = userBrowseTrack;
    if (bd_vid) {
      values.bd_vid = bd_vid;
    }
    const { data: resData } = await useFetch<{ success: boolean; code: number }>('/api/leads/submit', {
      method: 'POST',
      body: JSON.stringify({
        ...values,
      }),
    });
    if (resData.value?.success) {
      Message.success('提交成功');
      MyForm.value?.resetFields();
      form.landing = false;
      form.contract = false;
      form.service = false;
      form.globalRecruitment = false;
    } else {
      const code = resData.value?.code;
      Message.error(code === 4018 ? '您已经提交过资料，无需重复提交' : '访问人数过多，请稍后重试');
    }
  } catch (error) {
    Message.error('访问人数过多，请稍后重试');
  } finally {
    btnLoading.value = false;
  }
}

function handleRequestParams() {
  const temp: string[] = [];
  const { landing, contract, service } = form;
  if (landing) {
    temp.push('EMPLOYEE_LANDING');
  }
  if (contract) {
    temp.push('CONTRACTOR');
  }
  if (service) {
    temp.push('PAYROLL_PAYMENT');
  }
  return temp.join(',');
}

const mobileRule = ref([
  {
    required: true,
    match: /^(13[0-9]|14[01456879]|15[0-35-9]|16[2567]|17[0-8]|18[0-9]|19[0-35-9])\d{8}$/,
    message: t('请输入合法的手机号'),
  },
]);

onMounted(() => {
  form.targetCountry = props.targetCountry || '';
  userBrowseTrack = getUserBrowseTrack(props.entry || '') || '未知';
});
</script>

<style lang="less" scoped>
.form-wrapper {
  width: 320px;
  background-color: #fff;
  padding: 24px;
  border-radius: 8px;
  box-sizing: border-box;
  border-top: 4px solid #537dff;
  flex-shrink: 0;
  height: fit-content;
  .form-title {
    font-weight: 500;
    font-size: 18px;
    line-height: 22px;
    color: #000000;
    text-align: center;
    margin-bottom: 12px;
  }

  .form {
    :deep(.arco-input-wrapper) {
      height: 40px;
      background-color: #fff;
      border: 1px solid #c9cdd4;
      border-radius: 4px;
    }

    :deep(.arco-form-item-label) {
      font-weight: 400;
      font-size: 18px;
      line-height: 22px;
      color: #1d2129;
      display: flex;
      flex-direction: row-reverse;
    }

    :deep(.arco-icon-hover) {
      &::before {
        display: none;
      }
    }

    :deep(.arco-input) {
      font-size: 16px;
    }

    :deep(.country-select) {
      min-height: 40px;
      background-color: #fff;
      border: 1px solid #c9cdd4;
      border-radius: 4px;
      position: relative;

      .arco-select-div-tag {
        background-color: var(--color-fill-2);
      }

      .arco-select-div-inner {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
      }
    }

    .items {
      width: 100%;
      display: flex;
      justify-content: space-between;
      gap: 16px;
      flex-wrap: wrap;

      .item_radio_box {
        width: 128px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        border: 1px solid #c9cdd4;
        border-radius: 4px;
        padding: 0 18px 0 12px;
        box-sizing: border-box;
        cursor: pointer;
        flex-shrink: 0;
        user-select: none;

        :deep(.text) {
          color: #1d2129;
          font-weight: 400;
          font-size: 14px;
          line-height: 22px;
        }
      }

      .selected {
        border: 1px solid #165dff;
      }
    }

    .submit_button {
      box-sizing: border-box;
      display: block;
      background: #165dff;
      border-radius: 4px;
      margin: 4px auto 0px;
      width: 100%;
      height: 38px;
      font-weight: 500;
      font-size: 16px;
      color: #ffffff;
    }
  }
}
@media screen and (max-width: 800px) {
  .form-wrapper {
    width: calc(100% - 32px);
    margin: 0 16px;
    background-color: #eff3fb;
    .form {
      :deep(.arco-form-item-label) {
        font-size: 16px;
      }
      :deep(.arco-input-wrapper) {
        border: 1px solid #e5e7eb;
      }
      .items {
        display: grid;
        grid-template-columns: 1fr 1fr;
        .item_radio_box {
          width: auto;
          background-color: #fff;
          border: 1px solid #e5e7eb;
        }
      }
    }
  }
}
</style>
